<template>
  <div v-if="isVisible">
    <div v-if="mode === 'popup'" class="overlay">
      <div class="popup">
        <button @click="handleClosePopup" class="close-button">&times;</button>
        <div class="popup-content">
          <h2 class="title">Notice of Service Closure</h2>
          <p class="text">Thank you for using <strong>rebless.clinic</strong>.</p>
          <p class="text">
            Please note that the platform will be discontinued on <strong>February 28, 2026</strong>,
            as it was launched as a proof-of-concept (POC) service.
          </p>
          <p class="text">
            Access will remain available until the closure date.
            We recommend backing up any necessary information before the service ends.
          </p>
          <p class="text">
            For inquiries or support, please contact <a href="mailto:contact@hroboticsinc.com" class="link">contact@hroboticsinc.com</a>.
          </p>
          <p class="last-text">We sincerely appreciate your support and understanding.</p>
        </div>
        <div class="popup-footer">
          <button @click="handleClosePopup" class="confirm-button">Close</button>
        </div>
      </div>
    </div>

    <div v-else-if="mode === 'banner'" class="banner" ref="bannerRef">
      <div class="banner-container">
        <div class="banner-content">
          <span class="banner-title">Notice of Service Closure: </span>
          <span class="banner-text">
            Our service will be discontinued on Feb 28, 2026. Please backup your data.
            Contact: <a href="mailto:contact@hroboticsinc.com" class="banner-link">contact@hroboticsinc.com</a>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ServiceTerminationNotice",
  data() {
    return {
      isVisible: false,
      mode: null, // 'popup' or 'banner'
    };
  },
  mounted() {
    this.checkVisibility();
    window.addEventListener("resize", this.updateMargin);
  },
  beforeDestroy() {
    this.cleanupStyles();
    window.removeEventListener("resize", this.updateMargin);
  },
  watch: {
    mode() {
      if (this.isVisible) this.updateMargin();
    },
    isVisible(val) {
      if (val) {
        this.updateMargin();
        this.updateOverflow();
      } else {
        this.cleanupStyles();
      }
    },
  },
  methods: {
    async checkVisibility() {
      // 1. Check Country
      let countryCode = this.getCookie("user_country");

      // If cookie missing, try to fetch it
      if (!countryCode) {
        try {
          const response = await fetch("https://ipwho.is/");
          const data = await response.json();
          if (data && data.country_code) {
            countryCode = data.country_code;
            // Set cookie to avoid re-fetching (matching logic in CopyrightSec)
            let domainAttr = "";
            if (window.location.hostname.includes("rebless.clinic")) {
              domainAttr = "; Domain=.rebless.clinic";
            }
            document.cookie = `user_country=${countryCode}; Path=/; Secure; SameSite=Lax; Max-Age=86400${domainAttr}`;
          }
        } catch (e) {
          console.error("Failed to fetch country", e);
          return;
        }
      }

      // Only show if strictly NOT 'KR'
      if (!countryCode || countryCode === "KR") {
        return;
      }

      // 2. Check "Seen" status
      const hasSeen = localStorage.getItem("service_closure_notice_seen");

      if (hasSeen) {
        this.mode = "banner";
      } else {
        this.mode = "popup";
      }
      this.isVisible = true;
    },
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
    },
    handleClosePopup() {
      localStorage.setItem("service_closure_notice_seen", "true");
      this.isVisible = false;
      // After closing popup, we don't show banner immediately.
      // It will show on next reload as 'banner'.
    },
    updateMargin() {
      if (this.mode === "banner" && this.isVisible && this.$refs.bannerRef) {
        const height = this.$refs.bannerRef.offsetHeight;
        document.body.style.marginTop = `${height}px`;
        const header = document.querySelector("nav.header");
        if (header) {
          header.style.top = `${height}px`;
        }
      }
    },
    updateOverflow() {
      if (this.mode === "popup" && this.isVisible) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    },
    cleanupStyles() {
      document.body.style.marginTop = "";
      document.body.style.overflow = "auto";
      const header = document.querySelector("nav.header");
      if (header) {
        header.style.top = "";
      }
    },
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.popup {
  background-color: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.3s ease-out;
  overflow: hidden;
  /* Reset text alignment/color basics if parent overrides */
  text-align: left;
  color: #444;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.close-button {
  position: absolute;
  top: 15px;
  right: 20px;
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  line-height: 1;
  z-index: 1;
}

.popup-content {
  padding: 40px 30px 20px;
  text-align: left;
}

.popup-footer {
  padding: 0 30px 30px;
  text-align: right;
}

.title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #1a1a1a;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
  display: inline-block;
}

.text {
  font-size: 16px;
  line-height: 1.6;
  color: #444;
  margin-bottom: 15px;
}

.last-text {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-top: 20px;
}

.link {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
}

.confirm-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.confirm-button:hover {
  background-color: #0056b3;
}

/* Banner Styles */
.banner {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: #2c3e50;
  color: #fff;
  z-index: 100000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
}

.banner-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.banner-content {
  font-size: 14px;
  line-height: 1.5;
}

.banner-title {
  font-weight: 700;
  color: #ff6b6b; /* Accent color */
  margin-right: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.banner-text {
  color: #ecf0f1;
}

.banner-link {
  color: #3498db;
  text-decoration: underline;
  margin-left: 5px;
}
</style>
